import React, { useRef, useState } from "react";
import { UploadCloud, CheckCircle2, X, Film, Image as ImgIcon } from "lucide-react";
import { chunkedUpload, errMsg, fileKind, fmtSize, MAX_MB } from "./api";

// Selects a file, validates type/size and uploads it in chunks. Calls onUploaded(fileInfo|null).
export const UploadField = ({ client, accept = "all", onUploaded, testid = "upload", label }) => {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const acceptAttr = {
    all: ".mp4,.mov,.jpg,.jpeg,.png,.webp,video/mp4,video/quicktime,image/jpeg,image/png,image/webp",
    video: ".mp4,.mov,video/mp4,video/quicktime",
    imagen: ".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp",
  }[accept];

  const pick = async (f) => {
    setError("");
    if (!f) return;
    const kind = fileKind(f.name);
    if (!kind || (accept !== "all" && kind !== accept)) {
      setError(accept === "imagen" ? "Solo imágenes JPG, PNG o WebP." : accept === "video" ? "Solo videos MP4 o MOV." : "Formato no permitido. Videos: MP4, MOV. Imágenes: JPG, PNG, WebP.");
      return;
    }
    if (f.size > MAX_MB[kind] * 1048576) {
      setError(`El archivo pesa ${fmtSize(f.size)}. Máximo ${MAX_MB[kind]} MB para ${kind}s.`);
      return;
    }
    setFile(f);
    setState("uploading");
    setProgress(0);
    onUploaded?.(null);
    try {
      const info = await chunkedUpload(client, f, setProgress);
      setState("done");
      onUploaded?.(info);
    } catch (e) {
      setState("error");
      setError(errMsg(e, "La subida falló. Revisá tu conexión y probá de nuevo."));
    }
  };

  const reset = () => {
    setFile(null);
    setState("idle");
    setProgress(0);
    setError("");
    onUploaded?.(null);
    if (ref.current) ref.current.value = "";
  };

  const Icon = file && fileKind(file.name) === "video" ? Film : ImgIcon;

  return (
    <div data-testid={testid}>
      <input ref={ref} type="file" accept={acceptAttr} className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid={`${testid}-input`} />
      {!file ? (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className="w-full border-2 border-dashed border-gray-300 hover:border-[#0A2463] bg-gray-50 hover:bg-[#0A2463]/5 py-8 px-4 flex flex-col items-center gap-2 text-[#4B5563] transition-colors"
          data-testid={`${testid}-pick`}
        >
          <UploadCloud size={28} className="text-[#0A2463]" />
          <span className="font-headings uppercase tracking-wide text-sm text-[#0A192F]">{label || "Elegir archivo"}</span>
          <span className="text-xs">
            {accept === "imagen" ? "JPG, PNG, WebP · hasta 50 MB" : accept === "video" ? "MP4, MOV · hasta 500 MB" : "Videos MP4/MOV hasta 500 MB · Imágenes JPG/PNG/WebP hasta 50 MB"}
          </span>
        </button>
      ) : (
        <div className="border border-gray-300 bg-white p-4">
          <div className="flex items-center gap-3">
            <Icon size={20} className="text-[#0A2463] shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-[#0A192F] truncate">{file.name}</div>
              <div className="text-xs text-[#6B7280]">{fmtSize(file.size)}</div>
            </div>
            {state === "done" && <CheckCircle2 size={20} className="text-emerald-600" data-testid={`${testid}-done`} />}
            {state !== "uploading" && (
              <button type="button" onClick={reset} className="text-[#6B7280] hover:text-red-600" data-testid={`${testid}-reset`}><X size={18} /></button>
            )}
          </div>
          {state === "uploading" && (
            <div className="mt-3">
              <div className="h-2 bg-gray-200 overflow-hidden">
                <div className="h-full bg-[#F5B400] transition-[width] duration-300" style={{ width: `${progress}%` }} />
              </div>
              <div className="text-xs text-[#4B5563] mt-1" data-testid={`${testid}-progress`}>
                {progress < 95 ? `Subiendo… ${progress}%` : "Guardando archivo…"} No cierres esta página.
              </div>
            </div>
          )}
        </div>
      )}
      {error && <p className="mt-2 text-sm text-red-600" data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
};

export default UploadField;
